<script setup>
// Portfolio growth and comparison with MOEX total return indexes (docs/performance/SPEC.md, decisions 4, 7).
// Two modes: value in roubles with the money put in, and time-weighted return against an index.
import axios from 'axios';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { intlLocale, t } from '../i18n';
import { formatMoney, formatNumber, formatSignedMoney, formatSignedPercent, signClass } from '../format';
import { INDEX_CODES, INFLATION_CODE, PERIODS, periodPoints, periodRange, periodSummary, viewSeries } from '../performance';
import { isPhone } from '../isPhone';
import { readUiState, writeUiState } from '../uiState';
import { CHIP_ROW, chipClass } from '../ui';
import InfoHint from './InfoHint.vue';

const props = defineProps({
  portfolio: {
    type: Object,
    required: true,
  },
  // Today's value with live prices; null when the view is not whole accounts.
  liveToday: {
    type: Number,
    default: null,
  },
});

// Colors checked for contrast and colour blindness on the dark surface (docs/performance/SPEC.md, decision 21).
// Inflation: from the same validated palette of the payout calendar (decision 28).
const COLORS = { portfolio: '#3987e5', index: '#c98500', invested: '#94a3b8', inflation: '#d55181' };
const POLL_MS = 15000;

const data = ref(null);
const failed = ref(false);
let timer = null;

async function load() {
  try {
    const { data: body } = await axios.get('/performance');
    data.value = body;
    failed.value = false;
  } catch {
    failed.value = true;
  }
  clearTimeout(timer);
  // The history of a just imported account is rebuilt in the background within a minute or so.
  if (data.value?.portfolios.some((p) => p.status === 'pending')) {
    timer = setTimeout(load, POLL_MS);
  }
}

onMounted(load);
onBeforeUnmount(() => clearTimeout(timer));
// "Refresh" re-imports the accounts and makes their history pending again.
watch(() => props.portfolio.synced_at, load);

function remembered(key, allowed, fallback) {
  const value = readUiState(key, fallback);
  return allowed.includes(value) ? value : fallback;
}
const mode = ref(remembered('performance.mode', ['value', 'return'], 'return'));
const period = ref(remembered('performance.period', PERIODS, '1y'));
const indexCode = ref(remembered('performance.index', INDEX_CODES, 'MCFTRR'));
watch(mode, (v) => writeUiState('performance.mode', v));
watch(period, (v) => writeUiState('performance.period', v));
const showInflation = ref(readUiState('performance.inflation', false) === true);
watch(indexCode, (v) => writeUiState('performance.index', v));
watch(showInflation, (v) => writeUiState('performance.inflation', v));

const partial = computed(() => props.portfolio.members.some((m) => m.uids));
const series = computed(() => (data.value ? viewSeries(data.value, props.portfolio, partial.value ? null : props.liveToday) : null));
const status = computed(() => {
  const ids = new Set(props.portfolio.members.map((m) => m.portfolio_id));
  const own = (data.value?.portfolios ?? []).filter((p) => ids.has(p.id));
  if (own.some((p) => p.status === 'pending')) {
    return 'pending';
  }
  return own.some((p) => p.status === 'error') ? 'error' : 'ready';
});

const inflationLevel = computed(() => {
  const level = data.value?.indexes?.[INFLATION_CODE];
  return level?.dates?.length ? level : null;
});

const range = computed(() => (series.value ? periodRange(period.value, data.value.today, series.value.start) : null));
const points = computed(() => (series.value && range.value
  ? periodPoints(
    series.value,
    data.value.indexes?.[indexCode.value] ?? null,
    range.value,
    showInflation.value ? inflationLevel.value : null,
  )
  : []));
const summary = computed(() => periodSummary(points.value));
// Rosstat publishes with a lag of one to two weeks: the line ends earlier than the period.
const inflationUntil = computed(() => {
  const last = inflationLevel.value?.dates.at(-1);
  return showInflation.value && last && range.value && last < range.value.end ? last : null;
});

const year = computed(() => Number((data.value?.today ?? '').slice(0, 4)) || new Date().getFullYear());
function periodLabel(key) {
  return key === 'lastYear' ? String(year.value - 1) : t(`performance.period.${key}`);
}

// Chart geometry: the width follows the card.
const box = ref(null);
const width = ref(600);
let observer = null;
onMounted(() => {
  observer = new ResizeObserver(([entry]) => { width.value = Math.max(260, entry.contentRect.width); });
  if (box.value) {
    observer.observe(box.value);
  }
});
watch(box, (el) => { if (el && observer) observer.observe(el); });
onBeforeUnmount(() => observer?.disconnect());

const height = computed(() => (isPhone.value ? 200 : 240));
const PAD = { top: 12, right: 8, bottom: 24, left: 8 };

const lines = computed(() => {
  const inflation = showInflation.value && inflationLevel.value;
  return mode.value === 'value'
    ? [
      { key: 'value', color: COLORS.portfolio, label: t('performance.legend.value'), get: (p) => p.value },
      { key: 'invested', color: COLORS.invested, label: t('performance.legend.invested'), get: (p) => p.invested, dashed: true },
      ...(inflation
        ? [{ key: 'investedReal', color: COLORS.inflation, label: t('performance.legend.investedReal'), get: (p) => p.investedReal, dashed: true, area: true }]
        : []),
    ]
    : [
      { key: 'portfolio', color: COLORS.portfolio, label: t('performance.legend.portfolio'), get: (p) => p.portfolio, money: (p) => p.income },
      { key: 'index', color: COLORS.index, label: indexCode.value, get: (p) => p.index, money: (p) => p.indexIncome },
      ...(inflation
        ? [{ key: 'inflation', color: COLORS.inflation, label: t('performance.legend.inflation'), get: (p) => p.inflation, money: (p) => p.inflationIncome, area: true }]
        : []),
    ];
});

const yDomain = computed(() => {
  let min = Infinity;
  let max = -Infinity;
  for (const p of points.value) {
    for (const line of lines.value) {
      const v = line.get(p);
      if (v !== null && Number.isFinite(v)) {
        min = Math.min(min, v);
        max = Math.max(max, v);
      }
    }
  }
  if (!Number.isFinite(min)) {
    return [0, 1];
  }
  if (mode.value === 'return') {
    // Zero stays in view: above and below it is the whole story.
    min = Math.min(min, 0);
    max = Math.max(max, 0);
  }
  const pad = (max - min) * 0.08 || Math.abs(max) * 0.1 || 1;
  return [min - pad, max + pad];
});

const plot = computed(() => ({
  x0: PAD.left,
  x1: width.value - PAD.right,
  y0: PAD.top,
  y1: height.value - PAD.bottom,
}));

function x(i) {
  const n = Math.max(1, points.value.length - 1);
  return plot.value.x0 + (i / n) * (plot.value.x1 - plot.value.x0);
}
function y(v) {
  const [lo, hi] = yDomain.value;
  return plot.value.y1 - ((v - lo) / (hi - lo)) * (plot.value.y1 - plot.value.y0);
}

// Lines with `area` (inflation) get a light fill down to the bottom of the plot, one shape per unbroken run.
const paths = computed(() => lines.value.map((line) => {
  let d = '';
  let area = '';
  let run = [];
  const closeRun = () => {
    if (line.area && run.length > 1) {
      const bottom = plot.value.y1.toFixed(1);
      area += `M${run[0][0]},${bottom}${run.map(([px, py]) => `L${px},${py}`).join('')}L${run.at(-1)[0]},${bottom}Z`;
    }
    run = [];
  };
  points.value.forEach((p, i) => {
    const v = line.get(p);
    if (v === null || !Number.isFinite(v)) {
      closeRun();
      return;
    }
    const point = [x(i).toFixed(1), y(v).toFixed(1)];
    d += `${run.length ? 'L' : 'M'}${point[0]},${point[1]}`;
    run.push(point);
  });
  closeRun();
  return { ...line, d, areaD: area };
}));

// Four recessive grid lines with labels on the left, inside the plot.
const yTicks = computed(() => {
  const [lo, hi] = yDomain.value;
  return [0.15, 0.4, 0.65, 0.9].map((f) => lo + (hi - lo) * f).map((v) => ({ v, y: y(v), label: yLabel(v) }));
});

const compactMoney = computed(() => new Intl.NumberFormat(intlLocale(), { notation: 'compact', maximumFractionDigits: 1 }));
function yLabel(v) {
  return mode.value === 'value' ? `${compactMoney.value.format(v)} ₽` : formatSignedPercent(v, Math.abs(yDomain.value[1] - yDomain.value[0]) < 4 ? 1 : 0);
}

function formatDate(date, withDay = true) {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString(intlLocale(), withDay
    ? { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }
    : { month: 'short', year: '2-digit', timeZone: 'UTC' });
}

const xTicks = computed(() => {
  const n = points.value.length;
  if (n < 2) {
    return [];
  }
  const count = isPhone.value ? 3 : 5;
  return Array.from({ length: count }, (_, k) => Math.round((k / (count - 1)) * (n - 1)))
    .map((i, k) => ({ x: x(i), label: formatDate(points.value[i].date, n <= 120), anchor: k === 0 ? 'start' : k === count - 1 ? 'end' : 'middle' }));
});

// Hover and touch: the nearest day gets a crosshair, dots and a tooltip.
const hover = ref(null);
function onMove(event) {
  const rect = event.currentTarget.getBoundingClientRect();
  const px = ((event.clientX - rect.left) / rect.width) * width.value;
  const n = points.value.length;
  if (n === 0) {
    return;
  }
  const i = Math.round(((px - plot.value.x0) / (plot.value.x1 - plot.value.x0)) * (n - 1));
  hover.value = Math.min(n - 1, Math.max(0, i));
}
const hovered = computed(() => (hover.value === null ? null : points.value[hover.value] ?? null));
const tooltipLeft = computed(() => {
  if (hover.value === null) {
    return 0;
  }
  const w = tooltipWidth.value;
  return Math.min(Math.max(0, x(hover.value) - w / 2), width.value - w);
});

const tooltipWidth = computed(() => (mode.value === 'return' ? (isPhone.value ? 236 : 260) : (isPhone.value ? 168 : 196)));

const wholeRubles = computed(() => new Intl.NumberFormat(intlLocale(), { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }));
function rubles(v, signed = false) {
  const text = wholeRubles.value.format(Math.abs(v));
  return signed ? `${v > 0.5 ? '+' : v < -0.5 ? '−' : ''}${text}` : text;
}

function tooltipValue(line, p) {
  const v = line.get(p);
  if (v === null) {
    return '—';
  }
  if (mode.value === 'value') {
    return formatMoney(v, 'RUB');
  }
  const money = line.money(p);
  return money === null ? formatSignedPercent(v) : `${formatSignedPercent(v)} · ${rubles(money, true)}`;
}

// "Your portfolio is ahead of MCFTRR by 89 133 ₽ and 9,3 %" (decision 23). Roubles depend on when the money
// came in, percent do not, so the two may disagree in sign — then the sentence says so.
function compare(percentPoints, money, keyPrefix, params) {
  const all = { ...params, money: rubles(money), percent: `${formatNumber(Math.abs(percentPoints), 1)} %` };
  const aheadPercent = percentPoints >= 0;
  const aheadMoney = money >= 0;
  if (aheadPercent === aheadMoney) {
    return { text: t(`${keyPrefix}${aheadPercent ? 'ahead' : 'behind'}`, all), tone: aheadPercent ? 'text-emerald-400' : 'text-amber-300' };
  }
  return { text: t(`${keyPrefix}${aheadPercent ? 'aheadPercentBehindMoney' : 'behindPercentAheadMoney'}`, all), tone: 'text-slate-300' };
}
const comparison = computed(() => {
  const s = summary.value;
  if (!s || s.index === null || s.indexIncome === null) {
    return null;
  }
  return compare(s.portfolio - s.index, s.income - s.indexIncome, 'performance.', { index: indexCode.value });
});
// "Your portfolio is ahead of inflation by …", on the last day inflation is known (decision 28).
const inflationSummary = computed(() => (showInflation.value ? summary.value?.inflation ?? null : null));
const inflationComparison = computed(() => {
  const s = inflationSummary.value;
  if (!s || s.income === null) {
    return null;
  }
  return compare(s.portfolio - s.percent, s.portfolioIncome - s.income, 'performance.inflation.', {});
});
const ariaLabel = computed(() => {
  if (!summary.value) {
    return t('performance.title');
  }
  return mode.value === 'value'
    ? t('performance.aria.value', { value: formatMoney(summary.value.value, 'RUB'), income: formatSignedMoney(summary.value.income, 'RUB') })
    : t('performance.aria.return', { portfolio: formatSignedPercent(summary.value.portfolio), index: indexCode.value, indexValue: formatSignedPercent(summary.value.index) });
});

const segment = (selected) => (selected
  ? 'rounded-md bg-slate-700 px-2.5 py-1 text-[0.75rem] font-medium text-slate-100'
  : 'rounded-md px-2.5 py-1 text-[0.75rem] text-slate-400 hover:text-slate-200');
</script>

<template>
  <section :class="isPhone ? 'rounded-3xl bg-slate-900 p-5' : 'min-w-0 rounded-lg border border-slate-700 bg-slate-900/40 p-4 sm:p-5'">
    <div class="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
      <h3
        class="flex items-center gap-2 font-semibold text-slate-100"
        :class="isPhone ? 'text-[1.0625rem]' : 'text-[0.8125rem] text-slate-200'"
      >
        {{ t('performance.title') }}
        <InfoHint :label="t('performance.title')">
          <p>{{ t('performance.hint.twr') }}</p>
          <p class="mt-2">
            {{ t('performance.hint.indexes') }}
          </p>
          <p class="mt-2">
            {{ t('performance.hint.inflation') }}
          </p>
        </InfoHint>
      </h3>
      <div
        class="flex rounded-lg bg-slate-800/70 p-0.5"
        role="tablist"
        :aria-label="t('performance.title')"
      >
        <button
          v-for="key in ['return', 'value']"
          :key="key"
          type="button"
          role="tab"
          :aria-selected="mode === key"
          :class="[segment(mode === key), isPhone ? 'min-h-9 px-3 text-[0.875rem]' : '']"
          @click="mode = key"
        >
          {{ t(`performance.mode.${key}`) }}
        </button>
      </div>
    </div>

    <div
      v-if="isPhone"
      :class="[CHIP_ROW, 'mt-3']"
    >
      <button
        v-for="key in PERIODS"
        :key="key"
        type="button"
        :class="chipClass(period === key)"
        :aria-pressed="period === key"
        @click="period = key"
      >
        {{ periodLabel(key) }}
      </button>
    </div>
    <div
      v-else
      class="mt-3 flex flex-wrap items-center justify-between gap-2"
    >
      <div class="flex flex-wrap gap-1">
        <button
          v-for="key in PERIODS"
          :key="key"
          type="button"
          :class="segment(period === key)"
          :aria-pressed="period === key"
          @click="period = key"
        >
          {{ periodLabel(key) }}
        </button>
      </div>
      <div class="flex flex-wrap items-center gap-x-4 gap-y-2">
        <label
          v-if="mode === 'return'"
          class="flex items-center gap-2 text-[0.75rem] text-slate-400"
        >
          {{ t('performance.index') }}
          <select
            v-model="indexCode"
            class="rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-[0.75rem] text-slate-200"
          >
            <option
              v-for="code in INDEX_CODES"
              :key="code"
              :value="code"
            >{{ t(`performance.indexName.${code}`) }}</option>
          </select>
        </label>
        <label
          v-if="inflationLevel"
          class="flex cursor-pointer items-center gap-2 text-[0.75rem] text-slate-400 hover:text-slate-200"
        >
          <input
            v-model="showInflation"
            type="checkbox"
            class="h-3.5 w-3.5 accent-[#d55181]"
          >
          {{ t('performance.withInflation') }}
        </label>
      </div>
    </div>
    <label
      v-if="isPhone && mode === 'return'"
      class="mt-3 flex items-center justify-between gap-3 text-[0.9375rem] text-slate-400"
    >
      {{ t('performance.index') }}
      <select
        v-model="indexCode"
        class="min-h-11 max-w-[65%] rounded-xl bg-slate-800 px-3 text-[0.9375rem] text-slate-100"
      >
        <option
          v-for="code in INDEX_CODES"
          :key="code"
          :value="code"
        >{{ t(`performance.indexName.${code}`) }}</option>
      </select>
    </label>
    <label
      v-if="isPhone && inflationLevel"
      class="mt-3 flex min-h-11 cursor-pointer items-center justify-between gap-3 text-[0.9375rem] text-slate-400"
    >
      {{ t('performance.withInflation') }}
      <input
        v-model="showInflation"
        type="checkbox"
        class="h-5 w-5 accent-[#d55181]"
      >
    </label>

    <p
      v-if="failed && !data"
      class="mt-5 text-[0.8125rem] text-rose-400"
    >
      {{ t('performance.loadFailed') }}
    </p>
    <p
      v-else-if="!series && status === 'pending'"
      class="mt-5 text-[0.8125rem] text-slate-400"
    >
      {{ t('performance.pending') }}
    </p>
    <p
      v-else-if="!series && data"
      class="mt-5 text-[0.8125rem] text-slate-400"
    >
      {{ status === 'error' ? t('performance.error') : t('performance.empty') }}
    </p>

    <template v-else-if="series && summary">
      <!-- Headline numbers of the period -->
      <div
        v-if="mode === 'return'"
        class="mt-4 flex flex-wrap items-end gap-x-6 gap-y-2 tabular-nums"
      >
        <div>
          <p class="flex items-center gap-1.5 text-[0.75rem] text-slate-400">
            <span
              class="inline-block h-0.5 w-3 rounded-full"
              :style="{ background: COLORS.portfolio }"
              aria-hidden="true"
            />{{ t('performance.legend.portfolio') }}
          </p>
          <p
            class="text-[1.375rem] font-semibold leading-7"
            :class="signClass(summary.portfolio)"
          >
            {{ formatSignedPercent(summary.portfolio) }}
          </p>
          <p
            class="text-[0.8125rem]"
            :class="signClass(summary.income)"
          >
            {{ rubles(summary.income, true) }}
          </p>
        </div>
        <div v-if="summary.index !== null">
          <p class="flex items-center gap-1.5 text-[0.75rem] text-slate-400">
            <span
              class="inline-block h-0.5 w-3 rounded-full"
              :style="{ background: COLORS.index }"
              aria-hidden="true"
            />{{ indexCode }}
          </p>
          <p class="text-[1.375rem] font-semibold leading-7 text-slate-100">
            {{ formatSignedPercent(summary.index) }}
          </p>
          <p
            v-if="summary.indexIncome !== null"
            class="text-[0.8125rem] text-slate-400"
          >
            {{ rubles(summary.indexIncome, true) }}
          </p>
        </div>
        <div v-if="inflationSummary">
          <p class="flex items-center gap-1.5 text-[0.75rem] text-slate-400">
            <span
              class="inline-block h-0.5 w-3 rounded-full"
              :style="{ background: COLORS.inflation }"
              aria-hidden="true"
            />{{ t('performance.legend.inflation') }}
          </p>
          <p class="text-[1.375rem] font-semibold leading-7 text-slate-100">
            {{ formatSignedPercent(inflationSummary.percent) }}
          </p>
          <p
            v-if="inflationSummary.income !== null"
            class="text-[0.8125rem] text-slate-400"
          >
            {{ rubles(inflationSummary.income, true) }}
          </p>
        </div>
      </div>
      <template v-if="mode === 'return'">
        <p
          v-if="comparison"
          class="mt-2 text-xs"
          :class="comparison.tone"
        >
          {{ comparison.text }}
        </p>
        <p
          v-if="inflationComparison"
          class="mt-1 text-xs"
          :class="inflationComparison.tone"
        >
          {{ inflationComparison.text }}
        </p>
      </template>
      <div
        v-else
        class="mt-4 flex flex-wrap items-end gap-x-6 gap-y-2 tabular-nums"
      >
        <div>
          <p class="text-[0.75rem] text-slate-400">
            {{ t('performance.legend.value') }}
          </p>
          <p class="text-[1.375rem] font-semibold leading-7 text-slate-100">
            {{ formatMoney(summary.value, 'RUB') }}
          </p>
        </div>
        <div>
          <p class="text-[0.75rem] text-slate-400">
            {{ t('performance.income') }}
          </p>
          <p
            class="text-[1.0625rem] font-semibold leading-7"
            :class="signClass(summary.income)"
          >
            {{ formatSignedMoney(summary.income, 'RUB') }}
          </p>
        </div>
        <div>
          <p class="text-[0.75rem] text-slate-400">
            {{ t('performance.netFlow') }}
          </p>
          <p class="text-[1.0625rem] leading-7 text-slate-300">
            {{ formatSignedMoney(summary.netFlow, 'RUB') }}
          </p>
        </div>
        <div v-if="inflationSummary && inflationSummary.investedReal !== null">
          <p class="text-[0.75rem] text-slate-400">
            {{ t('performance.legend.investedReal') }}
          </p>
          <p class="text-[1.0625rem] leading-7 text-slate-300">
            {{ formatMoney(inflationSummary.investedReal, 'RUB') }}
          </p>
        </div>
      </div>

      <div
        ref="box"
        class="relative mt-4"
      >
        <svg
          :width="width"
          :height="height"
          :viewBox="`0 0 ${width} ${height}`"
          class="block touch-pan-y select-none"
          role="img"
          :aria-label="ariaLabel"
          @pointermove="onMove"
          @pointerdown="onMove"
          @pointerleave="hover = null"
        >
          <g aria-hidden="true">
            <line
              v-for="tick in yTicks"
              :key="tick.v"
              :x1="plot.x0"
              :x2="plot.x1"
              :y1="tick.y"
              :y2="tick.y"
              stroke="#1e293b"
              stroke-width="1"
            />
            <line
              v-if="mode === 'return'"
              :x1="plot.x0"
              :x2="plot.x1"
              :y1="y(0)"
              :y2="y(0)"
              stroke="#475569"
              stroke-width="1"
            />
            <template
              v-for="line in paths"
              :key="`a${line.key}`"
            >
              <path
                v-if="line.areaD"
                :d="line.areaD"
                :fill="line.color"
                fill-opacity="0.12"
              />
            </template>
            <text
              v-for="tick in yTicks"
              :key="`l${tick.v}`"
              :x="plot.x0"
              :y="tick.y - 4"
              fill="#64748b"
              font-size="11"
            >{{ tick.label }}</text>
            <text
              v-for="tick in xTicks"
              :key="`x${tick.x}`"
              :x="tick.x"
              :y="height - 6"
              :text-anchor="tick.anchor"
              fill="#64748b"
              font-size="11"
            >{{ tick.label }}</text>
            <path
              v-for="line in paths"
              :key="line.key"
              :d="line.d"
              fill="none"
              :stroke="line.color"
              :stroke-width="line.dashed ? 1.5 : 2"
              :stroke-dasharray="line.dashed ? '4 4' : null"
              stroke-linejoin="round"
              stroke-linecap="round"
            />
            <template v-if="hovered">
              <line
                :x1="x(hover)"
                :x2="x(hover)"
                :y1="plot.y0"
                :y2="plot.y1"
                stroke="#64748b"
                stroke-width="1"
              />
              <template
                v-for="line in lines"
                :key="`d${line.key}`"
              >
                <circle
                  v-if="line.get(hovered) !== null"
                  :cx="x(hover)"
                  :cy="y(line.get(hovered))"
                  r="4"
                  :fill="line.color"
                  stroke="#0f172a"
                  stroke-width="2"
                />
              </template>
            </template>
          </g>
        </svg>
        <div
          v-if="hovered"
          class="pointer-events-none absolute top-0 z-10 rounded-lg border border-slate-700 bg-slate-950/95 px-3 py-2 text-[0.75rem] tabular-nums text-slate-300 shadow-xl"
          :style="{ left: `${tooltipLeft}px`, width: `${tooltipWidth}px` }"
        >
          <p class="mb-1 text-slate-400">
            {{ formatDate(hovered.date) }}
          </p>
          <p
            v-for="line in lines"
            :key="`t${line.key}`"
            class="flex items-center justify-between gap-2"
          >
            <span class="flex items-center gap-1.5">
              <span
                class="inline-block h-0.5 w-3 rounded-full"
                :style="{ background: line.color }"
              />{{ line.label }}
            </span>
            <span class="text-slate-100">{{ tooltipValue(line, hovered) }}</span>
          </p>
        </div>
      </div>

      <!-- Legend: identity never by color alone -->
      <div class="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[0.75rem] text-slate-400">
        <span
          v-for="line in lines"
          :key="`g${line.key}`"
          class="flex items-center gap-1.5"
        >
          <svg
            width="16"
            height="4"
            aria-hidden="true"
          ><line
            x1="1"
            x2="15"
            y1="2"
            y2="2"
            :stroke="line.color"
            stroke-width="2"
            :stroke-dasharray="line.dashed ? '3 3' : null"
            stroke-linecap="round"
          /></svg>
          {{ mode === 'return' && line.key === 'index' ? t(`performance.indexName.${indexCode}`) : line.label }}
        </span>
      </div>

      <ul class="mt-2 space-y-0.5 text-[0.75rem] text-slate-500">
        <li v-if="status === 'pending'">
          {{ t('performance.updating') }}
        </li>
        <li v-if="range.clipped && period !== 'all'">
          {{ t('performance.since', { date: formatDate(series.start) }) }}
        </li>
        <li v-if="partial">
          {{ t('performance.wholeAccounts') }}
        </li>
        <li v-if="inflationUntil">
          {{ t('performance.inflationUntil', { date: formatDate(inflationUntil) }) }}
        </li>
        <li v-if="series.mismatches > 0">
          {{ t('performance.mismatches', { count: series.mismatches }) }}
        </li>
      </ul>
    </template>
  </section>
</template>
